import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  History,
  Loader2,
  Send,
  Pencil,
  Upload,
  UserCircle2,
  X,
  XCircle
} from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Textarea } from './ui/Textarea';
import { Toast } from './ui/Toast';
import ToastPortal from './ToastPortal';
import { useFeatures } from '../contexts/FeaturesContext';

const PROFILE_FIELDS = [
  { key: 'nom', label: 'Nom', type: 'text' },
  { key: 'prenom', label: 'Prénom', type: 'text' },
  { key: 'sexe', label: 'Sexe', type: 'select' },
  { key: 'telephone', label: 'Téléphone', type: 'tel' },
  { key: 'email', label: 'Adresse e-mail', type: 'email' },
  { key: 'adresse', label: 'Adresse', type: 'text' },
  { key: 'ville', label: 'Ville', type: 'text' },
  { key: 'code_postal', label: 'Code postal', type: 'text' },
  { key: 'pays', label: 'Pays', type: 'text' },
  { key: 'nationalite', label: 'Nationalité', type: 'text' },
  { key: 'date_naissance', label: 'Date de naissance', type: 'date' },
  { key: 'lieu_naissance', label: 'Lieu de naissance', type: 'text' }
];

const SEX_OPTIONS = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' }
];

const HISTORY_PAGE_SIZE = 2;

const FIELD_LABELS = PROFILE_FIELDS.reduce((acc, field) => {
  acc[field.key] = field.label;
  return acc;
}, {});

const normalizeValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const statusMeta = (status) => {
  if (status === 'approuvee') {
    return {
      label: 'Approuvée',
      icon: CheckCircle,
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700'
    };
  }
  if (status === 'refusee') {
    return {
      label: 'Refusée',
      icon: XCircle,
      className: 'border-red-200 bg-red-50 text-red-700'
    };
  }
  return {
    label: 'En attente',
    icon: Clock,
    className: 'border-amber-200 bg-amber-50 text-amber-700'
  };
};

const createInitialForm = (profile = {}) => {
  return PROFILE_FIELDS.reduce((acc, field) => {
    const value = profile[field.key];
    acc[field.key] = field.type === 'date' && value ? String(value).slice(0, 10) : normalizeValue(value);
    return acc;
  }, {});
};

const buildChangedFields = (form, profile = {}) => {
  return PROFILE_FIELDS.filter((field) => {
    const currentValue = field.type === 'date' && profile[field.key]
      ? String(profile[field.key]).slice(0, 10)
      : normalizeValue(profile[field.key]);
    return normalizeValue(form[field.key]) !== currentValue;
  });
};

const displayValue = (value) => {
  const text = normalizeValue(value);
  return text || '-';
};

const ProfileDropdown = ({ value, options, onChange, placeholder = 'Sélectionner', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selectedOption = options.find((option) => String(option.value) === String(value || ''));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative mt-1" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-left text-sm text-slate-800 transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#246990] disabled:bg-gray-100 disabled:text-gray-500"
      >
        <span className={selectedOption ? 'truncate' : 'truncate text-slate-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={18} className={`flex-shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-[10020] mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="max-h-64 overflow-y-auto py-1">
            {options.map((option) => {
              const selected = String(option.value) === String(value || '');
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-[#eef7fb] ${selected ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'}`}
                >
                  <span className="truncate">{option.label}</span>
                  {selected && <Check size={16} className="flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileSettingsPopover = ({ user, onUserChange }) => {
  const { estActive } = useFeatures();
  const [open, setOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  // La fiche profil n'existe que pour une chose : demander la correction de son
  // propre etat civil. Cette demande part dans la file des demandes speciales du
  // Centre de Support, absente des licences sans circuit de validation. Sans
  // elle, la fiche n'afficherait qu'une lecture seule de sa propre fiche, sans
  // aucune action possible : on ne l'ouvre pas. Les ressources humaines
  // corrigent la fiche directement.
  const circuitValidation = estActive('module.comptabilite');
  const [context, setContext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(() => createInitialForm(user));
  const [motif, setMotif] = useState('');
  const [identityFile, setIdentityFile] = useState(null);
  const [historyPage, setHistoryPage] = useState(0);

  const profile = context?.employe || user || {};
  const fullName = [profile?.prenom, profile?.nom].filter(Boolean).join(' ').trim() || 'Utilisateur';
  const roleName = profile?.role_principal?.nom || user?.role_principal?.nom || 'Non défini';
  const pendingRequest = context?.demande_en_attente;
  const recentHistory = Array.isArray(context?.historique_recent) ? context.historique_recent : [];

  const changedFields = useMemo(() => buildChangedFields(form, profile), [form, profile]);
  const hasChange = changedFields.length > 0 || Boolean(identityFile);
  const historyPageCount = Math.max(1, Math.ceil(recentHistory.length / HISTORY_PAGE_SIZE));
  const clampedHistoryPage = Math.min(historyPage, historyPageCount - 1);
  const visibleHistory = recentHistory.slice(
    clampedHistoryPage * HISTORY_PAGE_SIZE,
    clampedHistoryPage * HISTORY_PAGE_SIZE + HISTORY_PAGE_SIZE
  );

  const showToastMessage = (message, type = 'success') => {
    setToast({ id: Date.now(), message, type });
  };

  const refreshConnectedUser = (employee) => {
    if (!employee) return;

    const nextUser = {
      ...(user || {}),
      ...employee,
      role_principal: employee.role_principal || user?.role_principal || null,
      groupe_securite: employee.groupe_securite || user?.groupe_securite || null
    };

    try {
      localStorage.setItem('user', JSON.stringify(nextUser));
    } catch (storageError) {
      console.error('Erreur mise à jour utilisateur local:', storageError);
    }

    onUserChange?.(nextUser);
  };

  const loadContext = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.employes.getMyProfileChangeContext();
      if (response?.success) {
        setContext(response.data || {});
        refreshConnectedUser(response.data?.employe);
      } else {
        setError(response?.message || response?.error || 'Chargement du profil impossible.');
      }
    } catch (loadError) {
      setError(loadError.message || 'Chargement du profil impossible.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (circuitValidation) loadContext();
  }, [circuitValidation]);

  useEffect(() => {
    if (open) {
      setHistoryPage(0);
      loadContext();
    }
  }, [open]);

  useEffect(() => {
    if (historyPage > historyPageCount - 1) {
      setHistoryPage(Math.max(0, historyPageCount - 1));
    }
  }, [historyPage, historyPageCount]);

  const openRequestDialog = () => {
    setForm(createInitialForm(profile));
    setMotif('');
    setIdentityFile(null);
    setOpen(false);
    setRequestOpen(true);
  };

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePopoverOpenChange = (nextOpen) => {
    if (nextOpen) {
      setOpen(true);
    }
  };

  const handleRequestOpenChange = (nextOpen) => {
    if (nextOpen) {
      setRequestOpen(true);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!motif.trim()) {
      showToastMessage('Le motif de la demande est obligatoire.', 'error');
      return;
    }

    if (!hasChange) {
      showToastMessage('Aucun changement détecté.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const body = new FormData();
      body.append('payload', JSON.stringify({
        ...form,
        motif: motif.trim()
      }));
      if (identityFile) {
        body.append('piece_identite', identityFile);
      }

      const response = await api.employes.createMyProfileChangeRequest(body);
      if (response?.success) {
        setRequestOpen(false);
        showToastMessage('Demande envoyée au centre de support.', 'success');
        await loadContext();
      } else {
        showToastMessage(response?.message || response?.error || 'Envoi de la demande impossible.', 'error');
      }
    } catch (submitError) {
      showToastMessage(submitError.message || 'Envoi de la demande impossible.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Le bandeau garde l'identite de la personne connectee - la retirer laisserait
  // une barre ou l'on ne sait plus qui l'on est - mais il ne s'ouvre plus.
  if (!circuitValidation) {
    return (
      <div className="flex h-9 max-w-[280px] items-center gap-3 rounded-lg bg-white/10 px-4 py-2 text-left text-white">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/10">
          <UserCircle2 size={16} className="text-white" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-4 text-white">{fullName}</span>
          <span className="block truncate text-xs font-medium uppercase leading-4 text-white/80">{roleName}</span>
        </span>
      </div>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 max-w-[280px] items-center gap-3 rounded-lg bg-white/10 px-4 py-2 text-left text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Ouvrir les paramètres du profil"
          >
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/10">
              <UserCircle2 size={16} className="text-white" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold leading-4 text-white">{fullName}</span>
              <span className="block truncate text-xs font-medium uppercase leading-4 text-white/80">{roleName}</span>
            </span>
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={10}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="z-[80] w-[390px] rounded-lg border border-slate-200 bg-white p-0 shadow-xl"
        >
          <div className="border-b border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990]/10 text-[#246990]">
                  <UserCircle2 size={22} />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-bold text-slate-900">{fullName}</h3>
                  <p className="truncate text-xs font-medium uppercase text-slate-500">{roleName}</p>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                {!pendingRequest && !isLoading && (
                  <button
                    type="button"
                    onClick={openRequestDialog}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-[#246990]/10 hover:text-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/30"
                    aria-label="Demander une modification"
                    title="Demander une modification"
                  >
                    <Pencil size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#246990]/30"
                  aria-label="Fermer les paramètres du profil"
                  title="Fermer"
                >
                  <X size={17} />
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-600">
                <Loader2 size={18} className="animate-spin" />
                Chargement du profil...
              </div>
            ) : (
              <div className="space-y-4">
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Profil RH</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Téléphone</div>
                      <div className="truncate font-semibold text-slate-800">{displayValue(profile.telephone)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">E-mail</div>
                      <div className="truncate font-semibold text-slate-800">{displayValue(profile.email)}</div>
                    </div>
                  </div>
                </div>

                {pendingRequest && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-amber-800">
                      <Clock size={16} />
                      Demande en attente
                    </div>
                    <p className="mt-2 text-xs leading-5 text-amber-800">
                      {pendingRequest.titre || 'Modification des informations personnelles'}
                    </p>
                    <div className="mt-2 text-xs font-medium text-amber-700">
                      Envoyée le {formatDateTime(pendingRequest.date_demande)}
                    </div>
                  </div>
                )}

                <div>
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                    <History size={14} />
                    Dernières demandes
                  </div>
                  {recentHistory.length > 0 ? (
                    <div className="space-y-2">
                      {visibleHistory.map((item) => {
                        const meta = statusMeta(item.statut);
                        const StatusIcon = meta.icon;
                        const changes = item.champs_modifies || item.payload_demande?.champs || {};
                        const labels = Object.values(changes)
                          .map((change) => change?.label)
                          .filter(Boolean);

                        return (
                          <div key={item.id} className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate text-xs font-semibold text-slate-800">
                                {labels.length ? labels.join(', ') : item.support_titre || 'Modification profil'}
                              </span>
                              <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${meta.className}`}>
                                <StatusIcon size={12} />
                                {meta.label}
                              </span>
                            </div>
                            <div className="mt-2 text-xs text-slate-500">{formatDateTime(item.date_demande)}</div>
                          </div>
                        );
                      })}
                      {historyPageCount > 1 && (
                        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                          <span className="font-medium">{recentHistory.length} demandes</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setHistoryPage(Math.max(0, clampedHistoryPage - 1))}
                              disabled={clampedHistoryPage === 0}
                              className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-[#246990]/40 hover:text-[#246990] disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label="Demandes précédentes"
                            >
                              <ChevronLeft size={15} />
                            </button>
                            <span className="min-w-[44px] text-center font-semibold text-slate-600">
                              {clampedHistoryPage + 1} / {historyPageCount}
                            </span>
                            <button
                              type="button"
                              onClick={() => setHistoryPage(Math.min(historyPageCount - 1, clampedHistoryPage + 1))}
                              disabled={clampedHistoryPage >= historyPageCount - 1}
                              className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-[#246990]/40 hover:text-[#246990] disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label="Demandes suivantes"
                            >
                              <ChevronRight size={15} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                      Aucune demande récente.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={requestOpen} onOpenChange={handleRequestOpenChange}>
        <DialogContent
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="max-h-[90vh] max-w-4xl grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-lg p-0"
        >
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 py-5">
            <DialogTitle className="text-lg font-bold text-slate-900">
              Demande de modification des informations personnelles
            </DialogTitle>
            <DialogDescription>
              Les changements seront transmis au centre de support avant application sur la fiche RH.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {PROFILE_FIELDS.map((field) => (
                  <label key={field.key} className={field.key === 'adresse' ? 'md:col-span-2' : ''}>
                    <span className="mb-1 block text-sm font-semibold text-slate-700">{field.label}</span>
                    {field.type === 'select' ? (
                      <ProfileDropdown
                        value={form[field.key] || ''}
                        options={SEX_OPTIONS}
                        onChange={(option) => updateField(field.key, option.value)}
                        placeholder="Sélectionner"
                        disabled={isSubmitting}
                      />
                    ) : (
                      <Input
                        type={field.type}
                        value={form[field.key] || ''}
                        onChange={(event) => updateField(field.key, event.target.value)}
                        className="border-slate-300 focus-visible:ring-[#246990]/30"
                        disabled={isSubmitting}
                      />
                    )}
                  </label>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <FileText size={16} />
                  Pièce d'identité
                </div>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <label className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                    <Upload size={16} />
                    Joindre un fichier
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      className="hidden"
                      disabled={isSubmitting}
                      onChange={(event) => setIdentityFile(event.target.files?.[0] || null)}
                    />
                  </label>
                  <span className="truncate text-sm text-slate-600">
                    {identityFile ? identityFile.name : 'PDF, JPG ou PNG'}
                  </span>
                </div>
              </div>

              <label>
                <span className="mb-1 block text-sm font-semibold text-slate-700">Motif de la demande</span>
                <Textarea
                  value={motif}
                  onChange={(event) => setMotif(event.target.value)}
                  className="min-h-[100px] border-slate-300 focus-visible:ring-[#246990]/30"
                  placeholder="Exemple : correction du prénom après mise à jour des documents officiels"
                  disabled={isSubmitting}
                />
              </label>

              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
                {hasChange ? (
                  <span>
                    Champs détectés : {changedFields.map((field) => FIELD_LABELS[field.key]).join(', ') || "Pièce d'identité"}
                  </span>
                ) : (
                  <span>Aucun changement détecté pour le moment.</span>
                )}
              </div>
            </div>

            <DialogFooter className="shrink-0 gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:justify-end sm:space-x-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRequestOpen(false)}
                className="h-11 rounded-lg border-slate-300 px-5 text-base font-semibold"
                disabled={isSubmitting}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-11 gap-2 rounded-lg bg-[#246990] px-5 text-base font-semibold text-white hover:bg-[#1f5b7d]"
              >
                {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                Envoyer au support
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {toast && (
        <ToastPortal>
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </ToastPortal>
      )}
    </>
  );
};

export default ProfileSettingsPopover;